/**
 * The roster strip (slice 11g): the eleven as one sideways row of 44 px sprite thumbnails, each
 * with the lineup slot it holds under it in a small tag, on the Team tab (a tap takes one out) and
 * the Moves tab (a tap picks whose moves show). Depth-chart order: the offense starters QB RB WR1
 * WR2 TE OL1 OL2, then the defense-only starters in their order, a divider, then the bench. A
 * two-way starter shows its offense slot. A long press on a thumb opens the Pokémon sheet. A pick
 * change proposes the unheld slots and keeps the held ones (slice 11h: `togglePick` →
 * `recommend`), so with eleven picked no tag is ever empty.
 */
import { DEFENSE_SLOTS, OFFENSE_SLOTS, type RosterSlot } from '@tdmons/sim';
import { ROSTER_SIZE, type PackIndex, type RosterState } from './state';

export type StripEntry =
  { kind: 'thumb'; index: number; tag: RosterSlot | 'BENCH' } | { kind: 'divider' };

/** The strip's order and tags, from the charts as they are. */
export function stripOrder(
  state: Pick<RosterState, 'picks' | 'offense' | 'defense'>,
): StripEntry[] {
  const out: StripEntry[] = [];
  const seen = new Set<number>();
  const add = (slots: readonly RosterSlot[], chart: Partial<Record<RosterSlot, number>>) => {
    for (const slot of slots) {
      const i = chart[slot];
      if (i === undefined || !state.picks[i] || seen.has(i)) continue;
      seen.add(i);
      out.push({ kind: 'thumb', index: i, tag: slot });
    }
  };
  add(OFFENSE_SLOTS, state.offense);
  add(DEFENSE_SLOTS, state.defense);
  const bench = state.picks.flatMap((_, i) => (seen.has(i) ? [] : [i]));
  if (bench.length > 0 && out.length > 0) out.push({ kind: 'divider' });
  for (const index of bench) out.push({ kind: 'thumb', index, tag: 'BENCH' });
  return out;
}

/** How long a press on a thumb is held before it opens the sheet instead of tapping. */
export const LONG_PRESS_MS = 500;
/** A finger that moves this far (CSS px) is scrolling the strip, not pressing a thumb. */
const MOVE_PX = 10;

/**
 * A tap calls `onTap`; a press held `LONG_PRESS_MS` calls `onLong` and swallows the click that
 * follows. Scrolling the row, lifting early or the browser taking the gesture cancels the press.
 */
function pressable(b: HTMLButtonElement, onTap: () => void, onLong: () => void): void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let fired = false;
  let x = 0;
  let y = 0;
  const cancel = () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  };
  b.addEventListener('pointerdown', (e) => {
    fired = false;
    x = e.clientX;
    y = e.clientY;
    cancel();
    timer = setTimeout(() => {
      timer = undefined;
      fired = true;
      onLong();
    }, LONG_PRESS_MS);
  });
  b.addEventListener('pointermove', (e) => {
    if (Math.hypot(e.clientX - x, e.clientY - y) > MOVE_PX) cancel();
  });
  for (const type of ['pointerup', 'pointercancel', 'pointerleave'] as const) {
    b.addEventListener(type, cancel);
  }
  b.addEventListener('contextmenu', (e) => e.preventDefault());
  b.addEventListener('click', () => {
    if (fired) fired = false;
    else onTap();
  });
}

export interface StripOptions {
  state: RosterState;
  pack: PackIndex;
  /** Frame 0 of a Pokémon's front sheet, the grid's loader. */
  sprite(id: string): HTMLElement;
  /** What a tap does, for the thumb's label: "Remove Alakazam", "Moves of Alakazam". */
  verb(name: string): string;
  onTap(index: number): void;
  onAbout(index: number, opener: HTMLElement): void;
  /** The thumb that is lit (the Moves tab's chosen one). */
  selected?: number;
  /** A thumb with a problem on this tab carries the red border. */
  problem?(index: number): boolean;
  /** Numbered empty boxes after the picks, up to eleven (the Team tab). */
  empties?: boolean;
}

/** The strip as a `.sideways` row; the caller tracks its fade and keeps its scroll. */
export function renderStrip(o: StripOptions): HTMLElement {
  const row = document.createElement('div');
  row.className = 'roster-thumbs sideways';
  for (const entry of stripOrder(o.state)) {
    if (entry.kind === 'divider') {
      const d = document.createElement('span');
      d.className = 'strip-divider';
      d.setAttribute('aria-hidden', 'true');
      row.append(d);
      continue;
    }
    const { index, tag } = entry;
    const pick = o.state.picks[index];
    if (!pick) continue;
    const name = o.pack.byId.get(pick.id)?.name ?? pick.id;
    const item = document.createElement('span');
    item.className = 'strip-item';
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'btn roster-thumb';
    b.dataset.index = String(index);
    b.append(o.sprite(pick.id));
    b.setAttribute('aria-label', `${o.verb(name)}, ${tag === 'BENCH' ? 'bench' : tag}`);
    if (o.selected !== undefined) b.setAttribute('aria-pressed', String(o.selected === index));
    if (o.problem?.(index)) b.classList.add('problem');
    pressable(
      b,
      () => o.onTap(index),
      () => o.onAbout(index, b),
    );
    const label = document.createElement('span');
    label.className = 'strip-tag';
    label.setAttribute('aria-hidden', 'true');
    label.textContent = tag;
    item.append(b, label);
    row.append(item);
  }
  if (o.empties) {
    for (let i = o.state.picks.length; i < ROSTER_SIZE; i += 1) {
      const item = document.createElement('span');
      item.className = 'strip-item';
      const empty = document.createElement('span');
      empty.className = 'roster-empty';
      empty.textContent = String(i + 1);
      item.append(empty);
      row.append(item);
    }
  }
  return row;
}
